import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  TableMetadata,
  useTable,
  Label,
  ColumnSortEnum,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchInstitutionCommittee } from "../../api/Committee/InstitutionCommitteeApi";
import { getSchoolYearCurrent } from "../../api/SchoolYear/SchoolYearApi";
import {
  ContentArea,
  ToolbarTop,
  SearchCard,
  InstitutionSelect,
  ListTable,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { InstitutionCodeBook } from "../../models";
import {
  CommitteeSearch,
  CommitteeSort,
  CommitteeStatus,
  CommitteeTypeEnum,
  InstitutionCommittee,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { ClaimType } from "../../models/User";
import { committeeDetailUrl, committeeEditUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const storageKey = "ExpertCommissionSearchTableState";

export function CommitteeList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();

  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const INITIAL_TABLE_STATE = useMemo(
    () =>
      TableUtils.CreateInitialTableState<CommitteeSearch>(
        storageKey,
        TableUtils.tableDefaultState(CommitteeSort.Code, ColumnSortEnum.DESC)
      ),
    []
  );
  const defaultInstitutionId: number | undefined = undefined;

  //INIT SEARCH FILTER
  const CREATE_INITIAL_COMMITTEE_SEARCH = useMemo(
    () => ({
      institutionId: INITIAL_TABLE_STATE.institutionId
        ? INITIAL_TABLE_STATE.institutionId
        : defaultInstitutionId,
      committeeTypeId: CommitteeTypeEnum.Institution,
    }),
    [INITIAL_TABLE_STATE.institutionId, defaultInstitutionId]
  );
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<InstitutionCommittee>();
  const [tableData, setTableData] =
    useState<PagedResult<InstitutionCommittee>>(defaultTableData);
  //SET SEARCH FILTERS
  const [expertCommissionSearch, setExpertCommissionSearch] =
    useState<CommitteeSearch>(CREATE_INITIAL_COMMITTEE_SEARCH);
  const [tableSearch, setTableSearch] = useState<CommitteeSearch>(
    CREATE_INITIAL_COMMITTEE_SEARCH
  );
  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<InstitutionCommittee> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "name",
        header: t("common.institutionList.institutionName"),
        accessor: (item: InstitutionCommittee) => {
          return <div>{item.institution?.name}</div>;
        },
        sortBy: CommitteeSort.Name,
      },
      {
        columnKey: "dateFromDateTo",
        header: t("common.expertCommission.dateFromDateTo"),
        accessor: (item: InstitutionCommittee) => {
          return (
            <div>
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </div>
          );
        },
        sortBy: CommitteeSort.DateFrom,
      },
      {
        columnKey: "status",
        header: t("common.status"),
        accessor: (item: InstitutionCommittee) => {
          return (
            <>
              <Label
                color={FormatUtils.getCommitteeStatusColor(item.statusId)}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                {statuses.find((v) => v.id === item.statusId)?.name}
              </Label>
            </>
          );
        },
        sortBy: CommitteeSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: InstitutionCommittee) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<CommitteeSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });
  //FILTER DATA
  const [isEditable, setIsEditable] = useState(false);
  const [isSelectLoaded, setIsSelectLoaded] = useState(false);
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();

  useEffect(() => {
    (async () => {
      const schoolYear = await getSchoolYearCurrent();
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertCommissionSearch]);

  useEffect(() => {
    if (!isSelectLoaded) return;
    (async () => {
      const request = TableUtils.GetSearchRequest<CommitteeSearch>(
        tableSearch,
        tableState
      );
      const itemList = await searchInstitutionCommittee(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, isSelectLoaded]);

  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<CommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(committeeDetailUrl(id!));
    }
  };
  const handleNewExpertCommissionClicked = () => {
    if (expertCommissionSearch.institutionId !== undefined) {
      TableUtils.SetSessionStorageItem<CommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(committeeEditUrl(0, expertCommissionSearch.institutionId));
    }
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleInstitutionChange = (val: number) => {
    const newExpertCommissionSearch: CommitteeSearch = {
      ...expertCommissionSearch,
      institutionId: val,
    };
    setExpertCommissionSearch(newExpertCommissionSearch);
  };

  const handleSelectOnLoad = (val?: InstitutionCodeBook[]) => {
    //Only if the user has assigned to only one main institution.
    if (val?.length === 1) {
      const newExpertCommissionSearch: CommitteeSearch = {
        ...expertCommissionSearch,
        institutionId: val[0].id,
      };
      setClearFilter(true);
      setExpertCommissionSearch(newExpertCommissionSearch);
      setTableSearch(newExpertCommissionSearch);
      search();
    }
    setIsSelectLoaded(true);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={
          expertCommissionSearch.institutionId !== undefined &&
          !filterChanged &&
          tableData.results?.find(
            (val) =>
              val.statusId === CommitteeStatus.NotDefined ||
              val.statusId === CommitteeStatus.ConfirmationRequired
          ) === undefined &&
          isEditable
            ? "show"
            : "disable"
        }
        showBack={"hide"}
        onClickAdd={handleNewExpertCommissionClicked}
        pageTitle={t("common.expertCommission.pageName")}
      />
      <SearchCard
        onClickSearch={() => {
          setTableSearch(expertCommissionSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setExpertCommissionSearch({
            institutionId: defaultInstitutionId,
            committeeTypeId: CommitteeTypeEnum.Institution,
          });
          setTableSearch({
            institutionId: defaultInstitutionId,
            committeeTypeId: CommitteeTypeEnum.Institution,
          });
          search();
        }}
        isFilterChanged={filterChanged}
      >
        <Row>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof expertCommissionSearch>("institutionId")}
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              value={expertCommissionSearch.institutionId}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser
              onLoaded={(e) => handleSelectOnLoad(e)}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable tableProps={tableProps} />
    </ContentArea>
  );
}
